/**
 * Tenant Settings Component
 * 
 * Allows users to switch between tenants they have access to.
 * Located in the Settings page, not as a global dropdown.
 */

import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Badge } from '@/components/ui';
import { Building2, Check } from 'lucide-react';
import { useI18n } from '@/i18n';

// =============================================================================
// TenantSettings Component
// =============================================================================

export function TenantSettings() {
  const { t } = useI18n();
  const { currentTenant, availableTenants, setCurrentTenant, isLoading } = useTenant();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('admin.tenantSettings.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-16 bg-muted rounded-lg" />
            <div className="h-16 bg-muted rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          {t('admin.tenantSettings.title')}
        </CardTitle>
        <CardDescription>
          {t('admin.tenantSettings.selectWorkspace')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {availableTenants.length === 0 ? (
          <p className="text-muted-foreground">
            {t('admin.tenantSettings.noAccess')}
          </p>
        ) : (
          <div className="space-y-3">
            {availableTenants.map((tenant) => (
              <div
                key={tenant.id}
                className={`flex items-center space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-accent transition-colors ${
                  currentTenant?.id === tenant.id ? 'border-primary bg-accent' : ''
                }`}
                onClick={() => setCurrentTenant(tenant)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCurrentTenant(tenant);
                  }
                }}
              >
                <div className="flex-shrink-0">
                  <div 
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      currentTenant?.id === tenant.id 
                        ? 'border-primary bg-primary' 
                        : 'border-muted-foreground'
                    }`}
                  >
                    {currentTenant?.id === tenant.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">{tenant.name}</p>
                      <p className="text-sm text-muted-foreground">{tenant.slug}</p>
                    </div>
                    {tenant.role && (
                      <Badge variant="outline" className="ml-2">
                        {t(`admin.permissions.tenantRoles.${tenant.role}`)}
                      </Badge>
                    )}
                  </div>
                </div>
                {currentTenant?.id === tenant.id && (
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
